<script setup lang="ts">
import greenInfoIcon from '@/assets/icons/green/info.svg'
import { useMenuPosition } from '@/composables/useMenuPosition'
import { computed, ref, useAttrs } from 'vue'
import Tooltip from './Tooltip.vue'

const props = withDefaults(
  defineProps<{
    id?: string
    label?: string
    required?: boolean
    modelValue?: string | number | boolean | Date
    multiline?: boolean
    error?: boolean
    disabled?: boolean
    checked?: boolean
    type?: string
    inputType?: string
    icon?: string
    onClickButton?: () => void
    description?: string
    tooltip?: string
    placeholder?: string
    labelValue?: boolean
    appendInner?: boolean
    prependInner?: boolean
    append?: boolean
    min?: string
    max?: string
    highlightColor?: string
    /** controla o counter explicitamente em tipos não numéricos */
    counter?: boolean | undefined
    allowNegative?: boolean
    maska?: object | null
  }>(),
  {
    id: '',
    label: '',
    required: false,
    type: 'text',
    inputType: 'text',
    modelValue: '',
    multiline: false,
    error: false,
    disabled: false,
    checked: false,
    icon: '',
    onClickButton: undefined,
    description: undefined,
    tooltip: undefined,
    placeholder: undefined,
    labelValue: false,
    appendInner: false,
    prependInner: false,
    append: false,
    min: undefined,
    max: undefined,
    highlightColor: undefined,
    counter: undefined,
    allowNegative: false,
    maska: null,
  }
)

defineOptions({ inheritAttrs: false }) // controlamos attrs que vão pro v-text-field

const emit = defineEmits(['update:modelValue', 'input', 'clickIcon'])
const attrs = useAttrs()
const dateTimeFocus = ref(false)

// tooltip (usa de verdade o composable e o ícone)
const { openMenu, closeMenu } = useMenuPosition()

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

/** Ajusta o tipo real do input para date/time (evita mask antes do focus) */
const computedInputType = computed(() => {
  if (props.type === 'time' && dateTimeFocus.value) return 'time'
  if (props.type === 'date' && dateTimeFocus.value) return 'date'
  if (props.type === 'time' || (props.type === 'date' && !value.value))
    return 'text'
  return props.type
})

/** Filtra attrs “perigosos” quando type === 'number' */
const forwarded = computed<Record<string, unknown>>(() => {
  const a: Record<string, unknown> = { ...(attrs as Record<string, unknown>) }
  if (computedInputType.value === 'number') {
    delete a.maxlength
    delete a.maxLength
    delete a.counter
  }
  return a
})

/** Habilita o counter apenas quando NÃO é number e (counter === true OU tem maxlength) */
const shouldShowCounter = computed(
  () =>
    computedInputType.value !== 'number' &&
    (props.counter === true || forwarded.value.maxlength != null)
)

const radioValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const checkboxValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})
</script>

<template>
  <div v-if="type !== 'radio' && type !== 'checkbox'" class="w-100">
    <v-text-field
      :id="id"
      v-model="value"
      v-maska="props.type === 'number' ? maska : ''"
      class="w-100"
      v-bind="forwarded"
      variant="outlined"
      density="comfortable"
      :label="label || undefined"
      :type="type === 'number' ? 'text' : computedInputType"
      :placeholder="placeholder"
      :persistent-placeholder="placeholder ? true : false"
      :disabled="disabled"
      :error="!!error"
      :error-messages="typeof error === 'string' ? error : ''"
      :hint="description"
      hide-details="auto"
      :persistent-hint="!!description"
      :color="highlightColor"
      :counter="shouldShowCounter"
      :maxlength="
        computedInputType === 'number'
          ? undefined
          : (forwarded.maxlength as any) || undefined
      "
      @input="$emit('input', $event)"
      @focus="dateTimeFocus = true"
      @blur="dateTimeFocus = false"
    >
      <!-- Label -->
      <!-- ✅ só injeta quando existe label -->
      <template v-if="label" #label>
        <label class="input-label">
          {{ label }}<span v-if="required">*</span>
        </label>
      </template>

      <!-- Prepend icon -->
      <template v-if="prependInner" #prepend-inner>
        <img :src="icon" alt="ícone" />
      </template>

      <!-- Append icon -->
      <template v-if="appendInner" #append-inner>
        <div>
          <img :src="icon" alt="ícone" width="24" />
        </div>
      </template>

      <!-- Tooltip e botão -->
      <template v-if="tooltip || append" #append>
        <div v-if="tooltip" class="tooltip-wrapper">
          <Tooltip :header-text="tooltip">
            <img
              id="tooltip"
              width="24"
              :src="greenInfoIcon"
              alt="info"
              @mouseenter="openMenu"
              @mouseleave="closeMenu"
            />
          </Tooltip>
        </div>
        <div v-if="append && icon">
          <button @click="$emit('clickIcon')">
            <img :src="icon" alt="ícone" />
          </button>
        </div>
      </template>

      <!-- Slot do counter: renderiza só quando shouldShowCounter === true -->
      <template v-if="shouldShowCounter" #counter="slotProps">
        <span class="text-caption borderColor">{{ slotProps.counter }}</span>
      </template>
    </v-text-field>
    <!-- Radio -->
  </div>
  <div v-else>
    <v-radio-group
      v-if="type === 'radio'"
      v-model="radioValue"
      density="comfortable"
      :hide-details="true"
    >
      <v-radio
        :value="value"
        :label="label"
        :hide-details="true"
        v-bind="forwarded"
        :disabled="disabled"
        color="green"
        @input="$emit('input', $event)"
      />
    </v-radio-group>
    <v-checkbox
      v-if="type === 'checkbox'"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="checkboxValue"
      :label="label"
      :error="!!error"
      :error-messages="typeof error === 'string' ? error : ''"
      hide-details
      color="green"
      density="compact"
      class="ma-0 pa-0"
      :ripple="false"
      :disabled="disabled"
      @input="$emit('input', $event)"
    />
  </div>
</template>

<style scoped>
.input-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--v-theme-grey-2);
  display: inline-block;
}

.tooltip-wrapper {
  position: relative;
}
.tooltip-content {
  position: absolute;
  z-index: 50;
  margin-top: 8px;
  width: max-content;
  max-width: 30rem;
  background-color: #424242;
  color: #fff;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Figma visual layout */
:deep(.v-input.v-input--outlined) {
  height: 56px;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid;
}

:deep(.v-input__control) {
  min-height: auto;
  align-items: center;
}
:deep(.v-field__input) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Força largura total em inputs desabilitados */
:deep(.v-input input[disabled]) {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  display: block !important;
  box-sizing: border-box !important;
}

:deep(.v-input__details) {
  display: flex;
  overflow: visible;
  align-items: start;
  gap: 4px;
  width: 100%;
  padding-inline: 0 !important;
}

:deep(.v-messages) {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.borderColor, .v-counter) {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>
